import {useEffect, useRef} from "react";
import moment from "moment";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "./index.scss";

const matchesOf = (round) =>
	(round?.matches || [])
		.map((m) => m.match || m)
		.slice()
		.sort(
			(a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf(),
		);
const roundTitle = (round) =>
	/^\d+$/.test(round?.name || "")
		? `Giornata ${round.name}`
		: round?.name || `Giornata ${round?.roundNumber}`;
const dateLabel = (round) => {
	const dates = matchesOf(round)
		.map((m) => m.startDate)
		.filter((d) => d && moment(d).isValid());
	const first = round?.startDate || dates[0];
	const last = round?.endDate || dates[dates.length - 1];
	return first
		? moment(first).format("DD/MM/YYYY") +
				(last && !moment(first).isSame(last, "day")
					? " - " + moment(last).format("DD/MM/YYYY")
					: "")
		: "Date da definire";
};
const defaultRound = (rounds) => {
	const now = moment();
	const dated = rounds
		.flatMap((r) =>
			matchesOf(r)
				.filter((m) => m.startDate)
				.map((m) => ({round: r, date: moment(m.startDate)})),
		)
		.filter((x) => x.date.isValid())
		.sort((a, b) => a.date.valueOf() - b.date.valueOf());
	return (
		dated.find((x) => x.date.isSameOrAfter(now, "day"))?.round ||
		dated[dated.length - 1]?.round ||
		rounds[0]
	);
};

export default function CompetitionEditionCalendar({
	edition,
	renderMatch,
	renderRoundActions,
	renderAddRound,
	renderAddMatch,
	selection,
	onSelectionChange: setSelection,
}) {
	const roundsRef = useRef(null);
	const groups = (edition.groups || [])
		.slice()
		.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
	const group = groups.find((g) => g.id === selection.groupId) || groups[0];
	const rounds = (group?.rounds || [])
		.slice()
		.sort((a, b) => a.roundNumber - b.roundNumber);
	const round =
		rounds.find((r) => r.id === selection.roundId) || defaultRound(rounds);
	const index = rounds.findIndex((r) => r.id === round?.id);
	const unassigned = selection.unassigned || !rounds.length;
	useEffect(() => {
		const container = roundsRef.current;
		if (!container) return;
		const revealSelection = () => {
			const active = container.querySelector('[aria-pressed="true"]');
			if (!active) return;
			const bounds = container.getBoundingClientRect();
			const button = active.getBoundingClientRect();
			if (button.left < bounds.left)
				container.scrollLeft -= bounds.left - button.left + 4;
			else if (button.right > bounds.right)
				container.scrollLeft += button.right - bounds.right + 4;
		};
		revealSelection();
		const observer = new ResizeObserver(revealSelection);
		observer.observe(container);
		return () => observer.disconnect();
	}, [round?.id, group?.id, unassigned]);

	const choose = (r) =>
		setSelection({groupId: group.id, roundId: r.id, unassigned: false});
	const matches = unassigned
		? (edition.unassignedMatches || [])
				.map((m) => m.match || m)
				.slice()
				.sort(
					(a, b) =>
						moment(a.startDate).valueOf() - moment(b.startDate).valueOf(),
				)
		: matchesOf(round);
	return (
		<section
			className="edition-calendar"
			aria-label="Calendario della competizione"
		>
			<div className="edition-calendar__toolbar">
				<div className="edition-calendar__group">
					{groups.length > 1 ? (
						<label>
							Girone
							<select
								aria-label="Girone"
								value={group.id}
								onChange={(e) => setSelection({groupId: e.target.value})}
							>
								{groups.map((g) => (
									<option key={g.id} value={g.id}>
										{g.name}
									</option>
								))}
							</select>
						</label>
					) : (
						<strong>{group?.name || "Calendario"}</strong>
					)}
				</div>
				<div className="edition-calendar__toolbar-actions">
					{group && renderAddRound(group)}
					<button
						type="button"
						className="edition-calendar__unassigned"
						aria-pressed={unassigned}
						onClick={() =>
							setSelection({...selection, unassigned: !unassigned})
						}
					>
						Da assegnare <b>{edition.unassignedMatches?.length || 0}</b>
					</button>
					{renderAddMatch()}
				</div>
			</div>
			{!!rounds.length && (
				<div
					className="edition-calendar__rounds"
					ref={roundsRef}
					role="group"
					aria-label="Giornate"
				>
					{rounds.map((r) => (
						<button
							type="button"
							key={r.id}
							aria-pressed={!unassigned && round?.id === r.id}
							aria-label={roundTitle(r)}
							title={`${roundTitle(r)} · ${dateLabel(r)}`}
							onClick={() => choose(r)}
						>
							{/^\d+$/.test(r.name || "") ? r.name : r.name || r.roundNumber}
						</button>
					))}
				</div>
			)}
			<section
				className="edition-calendar__panel"
				aria-label={unassigned ? "Partite da assegnare" : roundTitle(round)}
			>
				<header className="edition-calendar__panel-header">
					<div className="edition-calendar__navigation">
						{!unassigned && (
							<button
								type="button"
								className="edition-calendar__arrow"
								aria-label="Giornata precedente"
								disabled={index <= 0}
								onClick={() => choose(rounds[index - 1])}
							>
								<ChevronLeftIcon />
							</button>
						)}
						<div>
							<h3>
								{unassigned
									? "Partite da assegnare"
									: round
										? roundTitle(round)
										: "Nessuna giornata"}
							</h3>
							<span>
								{!unassigned && round ? dateLabel(round) + " · " : ""}
								{matches.length} {matches.length === 1 ? "partita" : "partite"}
							</span>
						</div>
						{!unassigned && (
							<button
								type="button"
								className="edition-calendar__arrow"
								aria-label="Giornata successiva"
								disabled={index < 0 || index >= rounds.length - 1}
								onClick={() => choose(rounds[index + 1])}
							>
								<ChevronRightIcon />
							</button>
						)}
					</div>
					{!unassigned && round && (
						<div className="cs-actions">
							{renderRoundActions({...round, groupId: group.id})}
						</div>
					)}
				</header>
				<div className="edition-calendar__matches">
					{matches.map((m) =>
						renderMatch(
							m,
							unassigned ? undefined : {...round, groupId: group.id},
						),
					)}
					{!matches.length && (
						<p className="edition-calendar__empty">
							{unassigned
								? "Nessuna partita da assegnare."
								: "Nessuna partita in questa giornata."}
						</p>
					)}
				</div>
			</section>
		</section>
	);
}
